import {
    useMutation,
    useQueryClient,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { reportService } from './reportService';
import { reportReceiverService } from './reportReceiverService';
import {
    ReportWithDetails,
    ReportReceiverWithUser,
    CreateReportInput,
    UpdateReportParams,
    UpdateReportContentParams,
} from '../types/reportTypes';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';

/**
 * 보고서 상세 조회 훅
 * @param reportId 보고서 ID
 * @returns 보고서 상세 정보
 */
export function useReport(
    reportId: string | undefined
): UseQueryResult<ReportWithDetails | null, Error> {
    return useSafeQuery<ReportWithDetails | null>({
        queryKey: ['report', reportId],
        queryFn: () =>
            reportId
                ? reportService.getReportById(reportId)
                : Promise.resolve(null),
        enabled: !!reportId,
    });
}

/**
 * 사용자(작성자)별 보고서 목록 조회 훅 (항상 수신자 정보 포함)
 * @param userId 사용자 ID
 * @param workspaceId 워크스페이스 ID (선택사항)
 * @param searchQuery 검색어 (선택사항)
 * @param options 추가 쿼리 옵션
 * @returns 보고서 목록 (수신자 정보 포함)
 */
export function useReportsByUserId(
    userId: string | undefined,
    workspaceId?: string,
    searchQuery?: string,
    options?: Partial<UseQueryOptions<ReportWithDetails[]>>
): UseQueryResult<ReportWithDetails[], Error> {
    return useSafeQuery<ReportWithDetails[]>({
        queryKey: [
            'reports',
            'user_with_receivers',
            userId,
            workspaceId,
            searchQuery,
        ],
        queryFn: () => {
            if (!userId) return Promise.resolve([]);

            return reportService.getReportsByUserIdWithReceivers(
                userId,
                workspaceId,
                searchQuery
            );
        },
        enabled: !!userId,
        ...options,
    });
}

/**
 * 수신자별 보고서 목록(수신자 정보 포함) 조회 훅
 * @param userId 사용자 ID
 * @param workspaceId 워크스페이스 ID (선택사항)
 * @param searchQuery 검색어 (선택사항)
 * @param options 추가 쿼리 옵션
 * @returns 보고서 목록
 */
export function useReportsWithReceiverByUserId(
    userId: string | undefined,
    workspaceId?: string,
    searchQuery?: string,
    options?: Partial<UseQueryOptions<ReportWithDetails[]>>
): UseQueryResult<ReportWithDetails[], Error> {
    return useSafeQuery<ReportWithDetails[]>({
        queryKey: [
            'reports',
            'receiver_with_details',
            userId,
            workspaceId,
            searchQuery,
        ],
        queryFn: () =>
            userId
                ? reportService.getReportByReceiverId(
                      userId,
                      workspaceId,
                      searchQuery
                  )
                : Promise.resolve([]),
        enabled: !!userId,
        ...options,
    });
}

// ==================================================
// Mutation Hooks - 보고서 CRUD 관련
// ==================================================

/**
 * 보고서 생성 뮤테이션 훅
 * @returns 보고서 생성 뮤테이션
 */
export function useCreateReport() {
    const queryClient = useQueryClient();

    return useMutation<ReportWithDetails, Error, CreateReportInput>({
        mutationFn: (params: CreateReportInput) =>
            reportService.createReport(params),
        onSuccess: (data: ReportWithDetails) => {
            void queryClient.invalidateQueries({ queryKey: ['reports'] });
            void queryClient.invalidateQueries({
                queryKey: ['report', data.id],
            });
        },
    });
}

/**
 * 보고서 업데이트 뮤테이션 훅
 * @returns 보고서 업데이트 뮤테이션
 */
export function useUpdateReport() {
    const queryClient = useQueryClient();

    return useMutation<ReportWithDetails, Error, UpdateReportParams>({
        mutationFn: async ({ reportId, updates }: UpdateReportParams) => {
            // 보고서 업데이트 수행
            const updatedReport = (await reportService.updateReport(
                reportId,
                updates
            )) as ReportWithDetails;

            // 보고서 내용이 변경된 경우 수신자의 읽음 상태 초기화
            if ('content' in updates || 'title' in updates) {
                try {
                    await reportReceiverService.resetReportReadStatus(reportId);
                    console.log(
                        '보고서 수정으로 인해 수신자 읽음 상태가 초기화되었습니다.'
                    );
                } catch (error) {
                    console.error('수신자 읽음 상태 초기화 중 오류:', error);
                    // 읽음 상태 초기화 실패해도 보고서 업데이트는 성공한 것으로 처리
                }
            }

            return updatedReport;
        },
        onSuccess: (data: ReportWithDetails) => {
            void queryClient.invalidateQueries({ queryKey: ['reports'] });
            void queryClient.invalidateQueries({
                queryKey: ['report', data.id],
            });
            void queryClient.invalidateQueries({
                queryKey: ['reports', 'receiver'],
            });
            void queryClient.invalidateQueries({
                queryKey: ['reportReceivers', data.id],
            });
        },
    });
}

/**
 * 보고서 내용 업데이트 뮤테이션 훅
 * @returns 보고서 내용 업데이트 뮤테이션
 */
export function useUpdateReportContent() {
    const queryClient = useQueryClient();

    return useMutation<ReportWithDetails, Error, UpdateReportContentParams>({
        mutationFn: async ({
            reportId,
            content,
            markdownContent,
        }: UpdateReportContentParams) => {
            // 보고서 내용 업데이트 수행
            const updatedReport = (await reportService.updateReportContent(
                reportId,
                content,
                markdownContent,
                {
                    is_draft: false,
                }
            )) as ReportWithDetails;

            // 보고서 내용이 변경되었으므로 수신자의 읽음 상태 초기화
            try {
                await reportReceiverService.resetReportReadStatus(reportId);
                console.log(
                    '보고서 내용 수정으로 인해 수신자 읽음 상태가 초기화되었습니다.'
                );
            } catch (error) {
                console.error('수신자 읽음 상태 초기화 중 오류:', error);
                // 읽음 상태 초기화 실패해도 보고서 업데이트는 성공한 것으로 처리
            }

            return updatedReport;
        },
        onSuccess: (data: ReportWithDetails) => {
            void queryClient.invalidateQueries({
                queryKey: ['report', data.id],
            });
            void queryClient.invalidateQueries({
                queryKey: ['reports', 'receiver'],
            });
            void queryClient.invalidateQueries({
                queryKey: ['reportReceivers', data.id],
            });
        },
    });
}

/**
 * 보낸 보고서 삭제 뮤테이션 훅 (소프트 삭제)
 * @returns 보고서 삭제 뮤테이션
 */
export function useDeleteReport() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (reportId: string) => reportService.deleteReport(reportId),
        onSuccess: (_, reportId) => {
            void queryClient.invalidateQueries({ queryKey: ['reports'] });
            void queryClient.invalidateQueries({
                queryKey: ['report', reportId],
            });
            void queryClient.invalidateQueries({
                queryKey: ['reports', 'user'],
            });
            void queryClient.invalidateQueries({
                queryKey: ['reports', 'receiver'],
            });
        },
    });
}
